import { secureGet, securePost, securePut, secureDelete } from '../utils/HttpUtils';
import { ReferrerName } from '../types/interfaces';

const apiUrls = {
  referrerNames: '/v1/private/referrer-names',
};

// Helper function para loggear información de endpoints
const logEndpointInfo = (method: string, url: string, data?: any, response?: any, error?: any) => {
  console.group(`[ReferrerNameService] ${method} ${url}`);
  if (data) {
    console.log('Request Body:', data);
  }
  if (response) {
    console.log('Response:', response);
  }
  if (error) {
    console.error('Error:', error);
  }
  console.groupEnd();
};

export const getReferrerNames = async (): Promise<{
  success: boolean;
  data?: ReferrerName[];
  message?: string;
  status?: number;
}> => {
  try {
    console.log('[ReferrerNameService] GET - Investigating endpoint:', apiUrls.referrerNames);
    const response = await secureGet(apiUrls.referrerNames);
    
    logEndpointInfo('GET', apiUrls.referrerNames, undefined, response);
    
    if (response.success && 'data' in response) {
      console.log('[ReferrerNameService] GET - Success. Data structure:', {
        isArray: Array.isArray(response.data),
        length: Array.isArray(response.data) ? response.data.length : 'N/A',
        firstItem: Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : 'N/A',
      });
      return {
        success: true,
        data: response.data as ReferrerName[],
      };
    } else {
      console.warn('[ReferrerNameService] GET - Response not successful:', response);
      return {
        success: false,
        message: 'Error al obtener los referidos',
        status: response.status,
      };
    }
  } catch (error) {
    console.error('[ReferrerNameService] GET - Error fetching referrer names:', error);
    return {
      success: false,
      message: 'Error al obtener los referidos',
    };
  }
};

export const createReferrerName = async (
  name: string,
  code: string
): Promise<{
  success: boolean;
  data?: ReferrerName;
  message?: string;
  status?: number;
}> => {
  try {
    const body = { name, code };
    console.log('[ReferrerNameService] POST - Attempting to create referrer:', body);
    
    const response = await securePost(apiUrls.referrerNames, body);
    
    logEndpointInfo('POST', apiUrls.referrerNames, body, response);
    
    if (response.success && 'data' in response) {
      console.log('[ReferrerNameService] POST - Success. Created referrer:', response.data);
      return {
        success: true,
        data: response.data as ReferrerName,
      };
    } else {
      // Manejar errores comunes cuando el endpoint no existe
      if (response.status === 404 || response.status === 405 || response.status === 501) {
        console.warn('[ReferrerNameService] POST - Endpoint not implemented yet. Status:', response.status);
        return {
          success: false,
          message: 'La funcionalidad de crear referidos aún no está disponible en el servidor',
          status: response.status,
        };
      }
      console.warn('[ReferrerNameService] POST - Response not successful:', response);
      return {
        success: false,
        message: response.message || 'Error al crear el referido',
        status: response.status,
      };
    }
  } catch (error: any) {
    console.error('[ReferrerNameService] POST - Error creating referrer name:', error);
    // Manejar errores de red o cuando el endpoint no existe
    if (error?.response?.status === 404 || error?.response?.status === 405 || error?.response?.status === 501) {
      return {
        success: false,
        message: 'La funcionalidad de crear referidos aún no está disponible en el servidor',
        status: error.response?.status,
      };
    }
    return {
      success: false,
      message: 'Error al crear el referido',
    };
  }
};

export const updateReferrerName = async (
  id: number,
  name: string,
  code: string
): Promise<{
  success: boolean;
  data?: ReferrerName;
  message?: string;
  status?: number;
}> => {
  try {
    const url = `${apiUrls.referrerNames}/${id}`;
    const body = { name, code };
    console.log('[ReferrerNameService] PUT - Attempting to update referrer:', { id, ...body });
    
    const response = await securePut(url, body);
    
    logEndpointInfo('PUT', url, body, response);
    
    if (response.success && 'data' in response) {
      console.log('[ReferrerNameService] PUT - Success. Updated referrer:', response.data);
      return {
        success: true,
        data: response.data as ReferrerName,
      };
    } else {
      // Manejar errores comunes cuando el endpoint no existe
      if (response.status === 404 || response.status === 405 || response.status === 501) {
        console.warn('[ReferrerNameService] PUT - Endpoint not implemented yet. Status:', response.status);
        return {
          success: false,
          message: 'La funcionalidad de editar referidos aún no está disponible en el servidor',
          status: response.status,
        };
      }
      console.warn('[ReferrerNameService] PUT - Response not successful:', response);
      return {
        success: false,
        message: response.message || 'Error al actualizar el referido',
        status: response.status,
      };
    }
  } catch (error: any) {
    console.error('[ReferrerNameService] PUT - Error updating referrer name:', error);
    // Manejar errores de red o cuando el endpoint no existe
    if (error?.response?.status === 404 || error?.response?.status === 405 || error?.response?.status === 501) {
      return {
        success: false,
        message: 'La funcionalidad de editar referidos aún no está disponible en el servidor',
        status: error.response?.status,
      };
    }
    return {
      success: false,
      message: 'Error al actualizar el referido',
    };
  }
};

export const deleteReferrerName = async (
  id: number
): Promise<{
  success: boolean;
  message?: string;
  status?: number;
}> => {
  try {
    const url = `${apiUrls.referrerNames}/${id}`;
    console.log('[ReferrerNameService] DELETE - Attempting to delete referrer:', id);
    
    const response = await secureDelete(url);
    
    logEndpointInfo('DELETE', url, { id }, response);
    
    if (response.success) {
      console.log('[ReferrerNameService] DELETE - Success. Deleted referrer:', id);
      return {
        success: true,
      };
    } else {
      // Manejar errores comunes cuando el endpoint no existe
      if (response.status === 404 || response.status === 405 || response.status === 501) {
        console.warn('[ReferrerNameService] DELETE - Endpoint not implemented yet. Status:', response.status);
        return {
          success: false,
          message: 'La funcionalidad de eliminar referidos aún no está disponible en el servidor',
          status: response.status,
        };
      }
      console.warn('[ReferrerNameService] DELETE - Response not successful:', response);
      return {
        success: false,
        message: response.message || 'Error al eliminar el referido',
        status: response.status,
      };
    }
  } catch (error: any) {
    console.error('[ReferrerNameService] DELETE - Error deleting referrer name:', error);
    // Manejar errores de red o cuando el endpoint no existe
    if (error?.response?.status === 404 || error?.response?.status === 405 || error?.response?.status === 501) {
      return {
        success: false,
        message: 'La funcionalidad de eliminar referidos aún no está disponible en el servidor',
        status: error.response?.status,
      };
    }
    return {
      success: false,
      message: 'Error al eliminar el referido',
    };
  }
};

